// Winclap Brain — shared type contracts between frontend and backend

import { BRAIN_CLIENT_PUBLIC } from "../brain-clients/public";
import { BRAIN_PROFILE_PUBLIC } from "../brain-profiles/public";
import { brainProfileDisplayName, brainProfileIdOfScope, type BrainProfilePublic } from "../brain-profile-config";
import type { BrainSourceId } from "../brain-sources";

// ─── Clients ─────────────────────────────────────────────────────────────────
// La fuente de verdad es el registro por cliente: shared/brain-clients/<id>.json
// (schema en shared/brain-client-config.ts, índices generados con `npm run
// brain:clients`). Esto es la vista `id → nombre` que ya importaban 15 módulos;
// un cliente nuevo NO se agrega acá sino como JSON. Se deriva del índice PÚBLICO
// (id, nombre, preguntas) porque este archivo entra al bundle del frontend: los
// canales, secretos y nombres de Capacity quedan en index.ts, solo backend. El
// pseudo-cliente `portfolio` (agregados por segmento de Capacity_Final) también vive ahí.
export const BRAIN_CLIENTS: Record<string, string> = Object.fromEntries(
  Object.values(BRAIN_CLIENT_PUBLIC).map((c) => [c.id, c.displayName])
);

export type BrainClientId = keyof typeof BRAIN_CLIENTS;

/**
 * Clientes REALES: el registro sin los pseudo-clientes (`portfolio`, que son
 * agregados por segmento de Capacity_Final, no un cliente al que se le pueda
 * exigir conversación indexada).
 *
 * Existía escrito a mano —`Object.keys(BRAIN_CLIENTS).filter((id) => id !== "portfolio")`—
 * en tres lugares de `routers/brain/admin.ts`, cada uno con el literal repetido.
 * Sale del flag `pseudo` del registro, así que un pseudo-cliente nuevo no obliga
 * a tocar nada.
 */
export const BRAIN_REAL_CLIENT_IDS: string[] = Object.values(BRAIN_CLIENT_PUBLIC)
  .filter((c) => !c.pseudo)
  .map((c) => c.id);

// ─── Áreas (Sol 17/09/2026) ───────────────────────────────────────────────────
// Además de clientes, el chat consulta ÁREAS de Winclap (People, Finance). Un
// área viaja en el mismo campo que el cliente (`area:people`): los chats
// persistentes, el guard client_mismatch y el streaming no cambian; el handler
// bifurca a un contexto propio (landing de People + community map, digest del
// P&L) en vez de buscar en Aurora.
export const BRAIN_AREA_PREFIX = 'area:';
export const BRAIN_AREAS = {
  people: 'People',
  finance: 'Finance',
  /** Pipeline comercial (HubSpot × Capacity): la ve quien tiene usuario en HubSpot; cortes por owner solo governance. */
  pipeline: 'Pipeline comercial',
} as const satisfies Record<string, string>;
/** Unión literal: los registros por área (gate, rol, corpus, telemetría) son exhaustivos al compilar. */
export type BrainAreaId = keyof typeof BRAIN_AREAS;

export function brainAreaOf(scopeId: string | null | undefined): BrainAreaId | null {
  if (!scopeId || !scopeId.startsWith(BRAIN_AREA_PREFIX)) return null;
  const id = scopeId.slice(BRAIN_AREA_PREFIX.length);
  // hasOwnProperty, no `in`: "area:constructor" no es un área (herencia de Object.prototype).
  return Object.prototype.hasOwnProperty.call(BRAIN_AREAS, id) ? (id as BrainAreaId) : null;
}

// ─── Todos los clientes (MCP general, Sol 27/09/2026) ─────────────────────────
// Pseudo-scope: la pregunta se busca en TODOS los clientes de Aurora a la vez
// (UNION de los índices parciales por cliente + BM25 global). Viaja en el mismo
// campo que el cliente, igual que las áreas, así el trace, la telemetría y las
// citas (que ya traen client_id) no cambian de forma. No es un cliente real.
export const BRAIN_ALL_CLIENTS_SCOPE = 'all';
export const isBrainAllClientsScope = (scopeId: string | null | undefined): boolean =>
  scopeId === BRAIN_ALL_CLIENTS_SCOPE;

// ─── Modo PML (perfiles cliente · BU, 19/10/2026) ─────────────────────────────
// Scope `pml:<cliente>.<bu>` del MCP general (`/pml rappi rts`, `ask_pml`): la
// consulta ve SOLO las fuentes del perfil (shared/brain-profile-config.ts). Viaja
// en el mismo campo que el cliente, igual que las áreas y `all`, así el trace, la
// telemetría y las citas no cambian de forma. El registro público (id, cliente,
// BU) entra al bundle; canales, casillas y spaces quedan en index.ts (backend).

/** El perfil PML de un scope `pml:…`, o null si no tiene esa forma o no existe. */
export function brainProfileOf(scopeId: string | null | undefined): BrainProfilePublic | null {
  const id = brainProfileIdOfScope(scopeId);
  if (!id) return null;
  return Object.prototype.hasOwnProperty.call(BRAIN_PROFILE_PUBLIC, id) ? BRAIN_PROFILE_PUBLIC[id] : null;
}

/** Nombre para mostrar de un scope del chat: cliente, área, perfil PML o todos los clientes. */
export function brainScopeName(scopeId: string): string {
  const area = brainAreaOf(scopeId);
  if (area) return BRAIN_AREAS[area];
  if (isBrainAllClientsScope(scopeId)) return 'Todos los clientes';
  const profile = brainProfileOf(scopeId);
  if (profile) return brainProfileDisplayName(profile, BRAIN_CLIENTS[profile.clientId] ?? profile.clientId);
  return BRAIN_CLIENTS[scopeId] ?? scopeId;
}

/** Cita de un área: no es una página del Brain sino un link interno (landing de People, dashboard de Finance). */
export const BRAIN_LINK_CITATION_PREFIX = 'link:';

export type BrainParaType = "project" | "area" | "resource" | "archive";

export interface BrainPage {
  id: string;
  clientId: string;
  title: string;
  content: string;
  /** Una de shared/brain-sources.ts (BRAIN_SOURCES). */
  source: BrainSourceId;
  paraType: BrainParaType;
  fecha: string; // ISO date
  visibility: "account_team" | "leadership" | "all";
  createdAt: string;
  updatedAt: string;
}

export interface BrainCitation {
  pageId: string;
  clientId: string;
  title: string;
  excerpt: string;
  source: BrainPage["source"];
  fecha: string;
  score: number;
}

export interface BrainQueryResult {
  answer: string;
  citations: BrainCitation[];
  clientId: string;
  query: string;
}

export interface BrainQueryInput {
  clientId: string;
  /** Modo PML: id del perfil (`rappi.rts`). La búsqueda se limita a las fuentes del perfil; `clientId` es el del perfil. */
  profileId?: string;
  question: string;
  language?: string; // BCP 47 locale, e.g. "es", "en", "pt"
  layerFilter?: "L1" | "L2"; // L1 = client intel, L2 = financial
  conversationHistory?: Array<{ role: "user" | "assistant"; content: string }>;
  attachedSkillPrompt?: string; // system prompt injected from an attached skill
  contextFileKeys?: string[];   // S3 keys of user-uploaded context files
  userEmail?: string;           // for cost logging
}

// ─── Persistent chat history ─────────────────────────────────────────────────

export interface BrainStoredMessage {
  messageId: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string; // ISO
  citations?: BrainCitation[]; // assistant only, trimmed for storage
  feedback?: "like" | "dislike"; // assistant only
}

export interface BrainConversationSummary {
  conversationId: string;
  clientId: string;
  title: string;
  createdAt: string;
  lastMessageAt: string;
  messageCount: number;
}

// Response shape of the brain-query Function URL (superset of BrainQueryResult
// — extra fields present only when the request persisted the turn)
export interface BrainQueryHttpResponse extends BrainQueryResult {
  conversationId?: string;
  title?: string; // only set when the conversation was just created
  userMessageId?: string;
  assistantMessageId?: string;
  conversationFull?: boolean; // answer returned but NOT saved — conversation hit the size ceiling
}

export interface AttachedContextFile {
  key: string;
  filename: string;
  uploading?: boolean;
}

export interface ContextContract {
  usesWinclapBrain: boolean;
  usesClientBrain: boolean;
  requiresClient: boolean;
  allowedTools?: string[];
  freshness?: "latest" | "last_30_days" | "historical_ok";
  citationsRequired?: boolean;
}
